import type { EditorDoc, NoGoZone, RoomDims } from '@roomtreatment/core';
import { wingsToPolygon, type PolyPt } from './polygon';

export const NOGO_MIN_SIZE_M = 0.2;

const EPS = 1e-7;

/** The room's true floor polygon: `footprint` (freeform editor) wins, then
 *  `wings` (rect + alcoves), else the plain `Lx × Ly` box. Edge `i` (vertex
 *  `i` to `i + 1`) is the wall a `wall:i` surface key names. The one
 *  definition: panel-surface mounts against it (as `getRoomPolygon`),
 *  room-flow sizes distances by it and av-compat measures a projector's throw
 *  to it — a wall index that meant one thing in one package and another in
 *  the next would put a screen on the wrong wall. (`NoGoZoneCanvas.tsx`'s
 *  local `floorPoly()` still follows the same precedence.) */
export function roomFloorPolygon(doc: {
  dims: RoomDims;
  footprint?: EditorDoc['footprint'];
  wings?: EditorDoc['wings'];
}): PolyPt[] {
  const { Lx, Ly } = doc.dims;
  if (doc.footprint && doc.footprint.length >= 3) {
    return doc.footprint.map((c) => ({ x: c.x, y: c.y }));
  }
  if (doc.wings && doc.wings.length) return wingsToPolygon(Lx, Ly, doc.wings);
  return [{ x: 0, y: 0 }, { x: Lx, y: 0 }, { x: Lx, y: Ly }, { x: 0, y: Ly }];
}

function pointOnSegment(p: PolyPt, a: PolyPt, b: PolyPt): boolean {
  const cross = (p.x - a.x) * (b.y - a.y) - (p.y - a.y) * (b.x - a.x);
  if (Math.abs(cross) > EPS) return false;
  const dot = (p.x - a.x) * (b.x - a.x) + (p.y - a.y) * (b.y - a.y);
  if (dot < -EPS) return false;
  const len2 = (b.x - a.x) ** 2 + (b.y - a.y) ** 2;
  return dot <= len2 + EPS;
}

/** Point-in-polygon (even-odd ray cast), boundary counted as inside — a corner
 *  sitting exactly on a wall is still part of the room, not excluded from it. */
export function pointInPolygon(p: PolyPt, poly: PolyPt[]): boolean {
  const n = poly.length;
  if (n < 3) return false;
  for (let i = 0; i < n; i++) {
    if (pointOnSegment(p, poly[i]!, poly[(i + 1) % n]!)) return true;
  }
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = poly[i]!.x, yi = poly[i]!.y;
    const xj = poly[j]!.x, yj = poly[j]!.y;
    const intersects = yi > p.y !== yj > p.y
      && p.x < ((xj - xi) * (p.y - yi)) / (yj - yi) + xi;
    if (intersects) inside = !inside;
  }
  return inside;
}

/** Where a finite segment `a1→a2` crosses `b1→b2`, or `null` if they don't
 *  meet (parallel/collinear segments are treated as non-crossing here — any
 *  overlap they'd contribute is already covered by the endpoint-inside tests
 *  in {@link intersectRectPolygon}). */
function segmentIntersection(a1: PolyPt, a2: PolyPt, b1: PolyPt, b2: PolyPt): PolyPt | null {
  const d = (a2.x - a1.x) * (b2.y - b1.y) - (a2.y - a1.y) * (b2.x - b1.x);
  if (Math.abs(d) < 1e-12) return null;
  const t = ((b1.x - a1.x) * (b2.y - b1.y) - (b1.y - a1.y) * (b2.x - b1.x)) / d;
  const s = ((b1.x - a1.x) * (a2.y - a1.y) - (b1.y - a1.y) * (a2.x - a1.x)) / d;
  if (t < -EPS || t > 1 + EPS || s < -EPS || s > 1 + EPS) return null;
  return { x: a1.x + t * (a2.x - a1.x), y: a1.y + t * (a2.y - a1.y) };
}

export function genNoGoId(): string {
  return typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `nogo-${Math.random().toString(36).slice(2)}`;
}

export function normRect(
  x0: number,
  y0: number,
  x1: number,
  y1: number,
): { x0: number; y0: number; x1: number; y1: number } {
  return {
    x0: Math.min(x0, x1),
    y0: Math.min(y0, y1),
    x1: Math.max(x0, x1),
    y1: Math.max(y0, y1),
  };
}

export function rectMeetsMinSize(
  r: { x0: number; y0: number; x1: number; y1: number },
  min = NOGO_MIN_SIZE_M,
): boolean {
  return r.x1 - r.x0 >= min && r.y1 - r.y0 >= min;
}

/** The boundary vertices of the true `rect ∩ poly` region: rect corners that
 *  land inside the room, room vertices that land inside the rect, and every
 *  point where a rect edge crosses a room wall. For a convex overlap (a
 *  rectangular room, or a straight wall) these four kinds of point are
 *  exactly the corners of the clipped rectangle. For a non-convex room (an
 *  L-shape's inner corner) the true intersection can be a non-rectangular
 *  (even multi-piece) region — this returns its full vertex set, not an
 *  ordered ring, since {@link clampRectToRoom} only needs the set's bounding
 *  box. Empty when the rect and the room don't overlap at all. */
export function intersectRectPolygon(
  rect: { x0: number; y0: number; x1: number; y1: number },
  poly: PolyPt[],
): PolyPt[] {
  if (poly.length < 3) return [];
  const rectPts: PolyPt[] = [
    { x: rect.x0, y: rect.y0 },
    { x: rect.x1, y: rect.y0 },
    { x: rect.x1, y: rect.y1 },
    { x: rect.x0, y: rect.y1 },
  ];
  const rectContains = (p: PolyPt) =>
    p.x >= rect.x0 - EPS && p.x <= rect.x1 + EPS && p.y >= rect.y0 - EPS && p.y <= rect.y1 + EPS;

  const pts: PolyPt[] = [];
  for (const p of rectPts) if (pointInPolygon(p, poly)) pts.push(p);
  for (const p of poly) if (rectContains(p)) pts.push(p);
  for (let i = 0; i < 4; i++) {
    const a1 = rectPts[i]!, a2 = rectPts[(i + 1) % 4]!;
    for (let j = 0; j < poly.length; j++) {
      const b1 = poly[j]!, b2 = poly[(j + 1) % poly.length]!;
      const hit = segmentIntersection(a1, a2, b1, b2);
      if (hit) pts.push(hit);
    }
  }
  return pts;
}

/** Clip a drawn rectangle to the room's true polygon footprint, not just its
 *  axis-aligned bounding box — the root bug this fixes: a rectangle drawn
 *  partially outside a non-rectangular (L-shaped/freeform) room used to be
 *  clamped only against `Lx`/`Ly`, so the part hanging outside the real wall
 *  got saved anyway.
 *
 *  `NoGoZone` is, and must stay, an axis-aligned rectangle: `zonesToExclusion`
 *  (packages/room-flow/src/placement.ts) maps it 1:1 onto the backend's
 *  `exclusion_zones` wire format, and `_points_outside_zones`
 *  (backend/admin_optimize.py) reads each zone as a plain `(xmin, zmin, xmax,
 *  zmax)` box — there is no polygon exclusion-zone shape on the wire. So this
 *  returns the bounding box of the true `rect ∩ poly` intersection rather
 *  than the intersection itself. For a rectangular room, or a rect straddling
 *  a single straight wall, that bounding box IS the exact intersection. Only
 *  for a non-convex room (an L-shape's inner corner) can the true
 *  intersection be non-rectangular — the bounding box there is the tightest
 *  axis-aligned rectangle that still contains the whole overlap, which is
 *  strictly smaller than the previous Lx/Ly clamp and never drops any real
 *  overlap, but can still include a sliver of the notch outside the room.
 *  Returns `null` when the rectangle doesn't overlap the room at all. */
export function clampRectToRoom(
  r: { x0: number; y0: number; x1: number; y1: number },
  room: RoomDims | PolyPt[],
): { x0: number; y0: number; x1: number; y1: number } | null {
  const poly = Array.isArray(room)
    ? room
    : [{ x: 0, y: 0 }, { x: room.Lx, y: 0 }, { x: room.Lx, y: room.Ly }, { x: 0, y: room.Ly }];
  const pts = intersectRectPolygon(r, poly);
  if (!pts.length) return null;
  let x0 = Infinity, y0 = Infinity, x1 = -Infinity, y1 = -Infinity;
  for (const p of pts) {
    if (p.x < x0) x0 = p.x;
    if (p.x > x1) x1 = p.x;
    if (p.y < y0) y0 = p.y;
    if (p.y > y1) y1 = p.y;
  }
  return { x0, y0, x1, y1 };
}

export function pointInNoGoZone(x: number, y: number, z: NoGoZone): boolean {
  return x >= z.x0 && x <= z.x1 && y >= z.y0 && y <= z.y1;
}

export function hitTestNoGoZone(
  zones: NoGoZone[],
  x: number,
  y: number,
): NoGoZone | null {
  for (let i = zones.length - 1; i >= 0; i--) {
    if (pointInNoGoZone(x, y, zones[i]!)) return zones[i]!;
  }
  return null;
}

export function draftToNoGoZone(
  draft: { x0: number; y0: number; x1: number; y1: number },
  room: RoomDims | PolyPt[],
): NoGoZone | null {
  const clamped = clampRectToRoom(normRect(draft.x0, draft.y0, draft.x1, draft.y1), room);
  if (!clamped || !rectMeetsMinSize(clamped)) return null;
  return { id: genNoGoId(), ...clamped };
}

export function defaultCornerNoGoZone(doc: EditorDoc): NoGoZone {
  const w = Math.min(1, doc.dims.Lx * 0.2);
  const d = Math.min(1, doc.dims.Ly * 0.2);
  return { id: genNoGoId(), x0: 0, y0: 0, x1: w, y1: d };
}

/** Snap listener yaw to the nearest 15°. */
export function snapYaw(rad: number, stepDeg = 15): number {
  const step = (stepDeg * Math.PI) / 180;
  return Math.round(rad / step) * step;
}
